import { BAR_SLOT_COUNT } from "./layout.ts";

/**
 * The ability bar's pages (P1.9-T133; ADR-061's amendment of 2026-10-07 and its note for P1.9-T133; ADR-121 decision
 * 8): where the area is too small for ten slots beside the chat (`barPaged` in `hud/area-slots.ts`) the bar shows five
 * at a time, slots 1 to 5 or 6 to 10 by place, and a swap control shows the other five. The rule names slots by index
 * and nothing else, so it holds whatever the slots hold, and a pointer, a key and a touch (P1.9-T51) read the same
 * answers. Every key acts on its slot whichever page shows; a key whose slot is not shown flashes the swap control.
 */

/**
 * How many slots a page shows.
 */
export const BAR_PAGE_SIZE = 5;

/**
 * How many pages the bar has.
 */
export const BAR_PAGE_COUNT = BAR_SLOT_COUNT / BAR_PAGE_SIZE;

/**
 * Throws unless a number is a whole number under a bound.
 *
 * @param value The number.
 * @param bound The bound.
 * @param what What the number names, for the message.
 * @throws RangeError when it is not a whole number from 0 to under the bound.
 */
function within(value: number, bound: number, what: string): void {
  if (!Number.isInteger(value) || value < 0 || value >= bound) {
    throw new RangeError(`no ${what} ${value}`);
  }
}

/**
 * The page a slot is on.
 *
 * @param slot The slot's index, from 0.
 * @returns Its page, from 0.
 * @throws RangeError when the bar has no such slot.
 * @example
 * pageOf(6);
 * // 1
 */
export function pageOf(slot: number): number {
  within(slot, BAR_SLOT_COUNT, "slot");
  return Math.floor(slot / BAR_PAGE_SIZE);
}

/**
 * The column a slot stands in while its page shows.
 *
 * @param slot The slot's index, from 0.
 * @returns Its column on its page, from 0.
 * @throws RangeError when the bar has no such slot.
 * @example
 * pageColumn(6);
 * // 1
 */
export function pageColumn(slot: number): number {
  return slot - pageOf(slot) * BAR_PAGE_SIZE;
}

/**
 * The slots a page shows, in order.
 *
 * @param page The page, from 0.
 * @returns Their indexes.
 * @throws RangeError when the bar has no such page.
 * @example
 * pageSlots(1);
 * // [5, 6, 7, 8, 9]
 */
export function pageSlots(page: number): readonly number[] {
  within(page, BAR_PAGE_COUNT, "page");
  return Array.from({ length: BAR_PAGE_SIZE }, (_, column) => page * BAR_PAGE_SIZE + column);
}

/**
 * The page the swap shows from a page.
 *
 * @param page The page shown, from 0.
 * @returns The other page.
 * @throws RangeError when the bar has no such page.
 */
export function otherPage(page: number): number {
  within(page, BAR_PAGE_COUNT, "page");
  return (page + 1) % BAR_PAGE_COUNT;
}

/**
 * What the bar shows: the page, whether the area pages the bar at all, and how many times a key for a slot not shown
 * has asked the swap control to flash.
 */
export interface PagesSnapshot {
  readonly page: number;
  readonly paged: boolean;
  readonly flash: number;
}

/**
 * The page shown, kept in memory for the session. A load shows slots 1 to 5; the page is kept while the area crosses
 * the size either way, so a window dragged larger and back shows what it showed; a reconnect shows slots 1 to 5 again
 * (`reset`, which the bar's own reset calls). Nothing is stored and nothing crosses the wire.
 */
export class BarPages {
  private snapshot: PagesSnapshot = { page: 0, paged: false, flash: 0 };
  private readonly listeners = new Set<() => void>();

  /**
   * Adds a listener told after each change.
   *
   * @param listener The listener.
   * @returns A function that removes it.
   */
  public readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * What the bar shows now; the same object until it changes.
   *
   * @returns The snapshot.
   */
  public readonly getSnapshot = (): PagesSnapshot => this.snapshot;

  /**
   * Sets whether the area pages the bar, keeping the page.
   *
   * @param paged True where the bar shows five at a time.
   */
  public setPaged(paged: boolean): void {
    if (paged !== this.snapshot.paged) {
      this.change({ ...this.snapshot, paged });
    }
  }

  /**
   * Shows the other page, while the bar pages.
   *
   * @returns True when the page changed; false where the bar shows all ten.
   */
  public swap(): boolean {
    if (!this.snapshot.paged) {
      return false;
    }
    this.change({ ...this.snapshot, page: otherPage(this.snapshot.page) });
    return true;
  }

  /**
   * Shows a page, while the bar pages: what placing calls so the page follows the slot it focuses (P2.8-T30).
   *
   * @param page The page, from 0.
   * @returns True when the page changed; false where the bar shows all ten or already shows it.
   * @throws RangeError when the bar has no such page.
   */
  public show(page: number): boolean {
    within(page, BAR_PAGE_COUNT, "page");
    if (!this.snapshot.paged || this.snapshot.page === page) {
      return false;
    }
    this.change({ ...this.snapshot, page });
    return true;
  }

  /**
   * Notes a slot's key pressed: while the bar pages and the slot is not on the page shown, the swap control flashes
   * once, so the player sees something not shown was used. The key's use itself is the fight's, on either page.
   *
   * @param slot The slot's index, from 0.
   * @throws RangeError when the bar has no such slot.
   */
  public keyed(slot: number): void {
    if (this.snapshot.paged && pageOf(slot) !== this.snapshot.page) {
      this.change({ ...this.snapshot, flash: this.snapshot.flash + 1 });
    }
  }

  /**
   * Shows slots 1 to 5 again, as after a load; the size is kept, since the area has not changed.
   */
  public reset(): void {
    if (this.snapshot.page !== 0) {
      this.change({ ...this.snapshot, page: 0 });
    }
  }

  /**
   * Replaces the snapshot and tells every listener.
   *
   * @param next The new snapshot.
   */
  private change(next: PagesSnapshot): void {
    this.snapshot = next;
    for (const listener of this.listeners) {
      listener();
    }
  }
}
